"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import { AlertBanner } from "@/components/terra/component/AlertBanner/AlertBanner";
import type { FormattedAlertBanner } from "@/components/formatters/MXPAlertBanner";
const STORAGE_PREFIX = "alert-banner-dismissed";
const CSS_VAR = "--alert-banner-height";
function dismissKey(banner: FormattedAlertBanner): string {
  return `${STORAGE_PREFIX}:${banner.id ?? "unknown"}`;
}
function readDismissed(banner: FormattedAlertBanner | null): boolean {
  if (!banner?.id || typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(dismissKey(banner)) === "1";
  } catch {
    return false;
  }
}
function persistDismiss(banner: FormattedAlertBanner): void {
  if (!banner.id || typeof window === "undefined") return;
  try {
    sessionStorage.setItem(dismissKey(banner), "1");
  } catch {
    // sessionStorage can throw in private mode — local dismiss still works.
  }
}
function setAlertOffset(px: number): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(CSS_VAR, `${px}px`);
}
type AlertBannersProps = {
  banner?: FormattedAlertBanner | null;
  /**
   * Which slot this is. `alert` comes from Site Config and is fixed above the
   * nav; `operational` comes from a page's Compose: SEO entry and renders in
   * the page itself, directly below the nav.
   */
  slot: "alert" | "operational";
};

/**
 * Client wrapper for one banner slot: session dismiss + CSS offset for the
 * fixed yellow Alert bar. Offset tracks the real banner height so a wrapped
 * mobile message does not cover the hamburger.
 */
export default function AlertBanners(t0) {
  const $ = _c(15);
  const {
    banner: t1,
    slot
  } = t0;
  const source = t1 === undefined ? null : t1;
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const bannerRef = useRef(null);
  const [checkedId, setCheckedId] = useState(null);
  if (source?.id && checkedId !== source.id) {
    setCheckedId(source.id);
    setSessionDismissed(readDismissed(source));
  } else {
    if (!source && checkedId !== null) {
      setCheckedId(null);
      setSessionDismissed(false);
    }
  }
  const banner = source && !sessionDismissed ? source : null;
  let t2;
  let t3;
  if ($[0] !== banner || $[1] !== slot) {
    t2 = () => {
      if (slot !== "alert") {
        return;
      }
      if (!banner) {
        setAlertOffset(0);
        return _temp;
      }
      const el = bannerRef.current;
      if (!el) {
        return;
      }
      const syncOffset = () => {
        setAlertOffset(Math.ceil(el.getBoundingClientRect().height));
      };
      syncOffset();
      const observer = new ResizeObserver(syncOffset);
      observer.observe(el);
      return () => {
        observer.disconnect();
        setAlertOffset(0);
      };
    };
    t3 = [banner, slot];
    $[0] = banner;
    $[1] = slot;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  if (!banner) {
    return null;
  }
  const t4 = slot === "alert" ? bannerRef : undefined;
  let t5;
  if ($[4] !== banner) {
    t5 = () => {
      persistDismiss(banner);
      setSessionDismissed(true);
    };
    $[4] = banner;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== banner.bannerType || $[7] !== banner.isActive || $[8] !== banner.isDismissible || $[9] !== banner.link || $[10] !== banner.message || $[11] !== banner.mobileMessage || $[12] !== t4 || $[13] !== t5) {
    t6 = <AlertBanner ref={t4} message={banner.message} mobileMessage={banner.mobileMessage} bannerType={banner.bannerType} link={banner.link} isDismissible={banner.isDismissible} isActive={banner.isActive} onDismiss={t5} />;
    $[6] = banner.bannerType;
    $[7] = banner.isActive;
    $[8] = banner.isDismissible;
    $[9] = banner.link;
    $[10] = banner.message;
    $[11] = banner.mobileMessage;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp() {
  return setAlertOffset(0);
}